import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { getErrorMessage } from "@/lib/errorUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  LogOut, 
  Users, 
  Building2, 
  Search,
  Copy,
  Check,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Wallet,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Clock,
  FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeTab, setActiveTab] = useState("partners");
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal states
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [transactionAmount, setTransactionAmount] = useState("");
  const [transactionDescription, setTransactionDescription] = useState("");
  const [processingPayoutId, setProcessingPayoutId] = useState(null);
  const [billingPartner, setBillingPartner] = useState(null);

  useEffect(() => {
    fetchPartners();
    fetchReferrals();
    fetchAllTransactions();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/staff/partners`, { headers: getAuthHeader() });
      setPartners(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei partner");
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/staff/referrals`, { headers: getAuthHeader() });
      setReferrals(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle segnalazioni");
    }
  };

  const handleUpdateReferralStatus = async (referralId, newStatus) => {
    try {
      await axios.put(`${API}/staff/referrals/${referralId}/status`, { status: newStatus }, { headers: getAuthHeader() });
      const labels = { in_gestione: "In gestione", sottoscrizione_avvenuta: "Sottoscrizione avvenuta", vendita_confermata: "Vendita confermata", perso: "Perso" };
      toast.success(`Stato aggiornato a "${labels[newStatus]}"`);
      fetchReferrals();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'aggiornamento dello stato"));
    }
  };

  const fetchAllTransactions = async () => {
    try {
      const response = await axios.get(`${API}/staff/transactions`, { headers: getAuthHeader() });
      setAllTransactions(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success("Codice copiato!");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error("Errore nel copiare il codice");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logout effettuato");
  };

  const openCreditModal = (partner) => {
    setSelectedPartner(partner);
    setTransactionAmount("");
    setTransactionDescription("");
    setShowCreditModal(true);
  };

  const openPayoutModal = (partner) => {
    setSelectedPartner(partner);
    setTransactionAmount("");
    setTransactionDescription("");
    setShowPayoutModal(true);
  };

  const handleAddCredit = async (e) => {
    e.preventDefault();
    if (!transactionAmount || parseFloat(transactionAmount) <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/staff/transactions/credit`, {
        user_id: selectedPartner.id,
        amount: parseFloat(transactionAmount),
        description: transactionDescription || "Commissione aggiunta"
      }, { headers: getAuthHeader() });
      
      toast.success(`€${transactionAmount} aggiunti a ${selectedPartner.first_name} ${selectedPartner.last_name}`);
      setShowCreditModal(false);
      fetchPartners();
      fetchAllTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'operazione"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessPayout = async (e) => {
    e.preventDefault();
    if (!transactionAmount || parseFloat(transactionAmount) <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/staff/transactions/payout`, {
        user_id: selectedPartner.id,
        amount: parseFloat(transactionAmount),
        description: transactionDescription || "Payout effettuato"
      }, { headers: getAuthHeader() });
      
      toast.success(`Payout di €${transactionAmount} effettuato per ${selectedPartner.first_name} ${selectedPartner.last_name}`);
      setShowPayoutModal(false);
      fetchPartners();
      fetchAllTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'operazione"));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPartners = partners.filter(partner => 
    partner.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.referral_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredReferrals = referrals.filter(referral =>
    referral.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    referral.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    referral.referred_by_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingPayouts = allTransactions.filter(t => t.type === 'payout_request' && t.status === 'pending');

  const handleAcceptPayout = async (transactionId) => {
    setProcessingPayoutId(transactionId);
    try {
      await axios.post(`${API}/staff/transactions/accept-payout/${transactionId}`, {}, { headers: getAuthHeader() });
      toast.success("Payout approvato con successo");
      fetchPartners();
      fetchAllTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'approvazione"));
    } finally {
      setProcessingPayoutId(null);
    }
  };

  const handleRejectPayout = async (transactionId) => {
    setProcessingPayoutId(transactionId);
    try {
      await axios.post(`${API}/staff/transactions/reject-payout/${transactionId}`, {}, { headers: getAuthHeader() });
      toast.success("Richiesta di payout rifiutata");
      fetchAllTransactions();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nel rifiuto"));
    } finally {
      setProcessingPayoutId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="staff-dashboard">
      {/* Background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[400px] bg-[#fcb900]/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[300px] bg-[#fc0301]/5 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className="border-b border-white/5 bg-black/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://aiclo.io/wp-content/uploads/2025/09/aiclo.svg" 
              alt="Aiclo" 
              className="h-6"
            />
            <div>
              <span className="text-xs bg-[#fcb900]/20 text-[#fcb900] px-2 py-1 rounded">Staff</span>
              <p className="text-sm text-muted-foreground mt-1">
                {user?.first_name} {user?.last_name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-foreground"
            data-testid="staff-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Esci
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 relative z-10">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#fcb900]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#fcb900]" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Partner Totali</p>
                <p className="text-2xl font-semibold" data-testid="total-partners">{partners.length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#fcb900]/20 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-[#fcb900]" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Saldo Totale Partner</p>
                <p className="text-2xl font-semibold text-gold-shimmer" data-testid="total-balance">
                  €{partners.reduce((sum, p) => sum + (p.balance || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Segnalazioni Totali</p>
                <p className="text-2xl font-semibold" data-testid="total-referrals">{referrals.length}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            className="glass-card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payout in Attesa</p>
                <p className="text-2xl font-semibold text-yellow-500" data-testid="pending-payouts">
                  {pendingPayouts.length}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cerca partner, email, codice referral o azienda..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-card border-white/10 h-11"
            data-testid="search-input"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-black/50 border border-white/10 mb-6">
            <TabsTrigger value="partners" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-partners">
              <Users className="w-4 h-4 mr-2" />
              Partner ({filteredPartners.length})
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-payouts">
              <Clock className="w-4 h-4 mr-2" />
              Richieste Payout ({pendingPayouts.length})
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-referrals">
              <Building2 className="w-4 h-4 mr-2" />
              Segnalazioni ({filteredReferrals.length})
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-[#fcb900]/20 data-[state=active]:text-white" data-testid="tab-transactions">
              <Wallet className="w-4 h-4 mr-2" />
              Movimenti ({allTransactions.length})
            </TabsTrigger>
          </TabsList>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <motion.div
              className="glass-card overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Table data-testid="partners-table">
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Partner</TableHead>
                    <TableHead>Contatti</TableHead>
                    <TableHead>Codice Referral</TableHead>
                    <TableHead>Saldo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPartners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Nessun partner trovato
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPartners.map((partner) => (
                      <TableRow key={partner.id} className="border-white/5">
                        <TableCell>
                          <div>
                            <p className="font-medium">{partner.first_name} {partner.last_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(partner.created_at).toLocaleDateString('it-IT')}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="flex items-center gap-2 text-sm">
                              <Mail className="w-3 h-3 text-muted-foreground" />
                              {partner.email}
                            </p>
                            <p className="flex items-center gap-2 text-sm">
                              <Phone className="w-3 h-3 text-muted-foreground" />
                              {partner.phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="px-2 py-1 rounded bg-[#fcb900]/10 text-[#fcb900] text-sm font-mono">
                              {partner.referral_code}
                            </code>
                            <button
                              onClick={() => copyCode(partner.referral_code)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                            >
                              {copiedCode === partner.referral_code ? (
                                <Check className="w-3 h-3 text-accent" />
                              ) : (
                                <Copy className="w-3 h-3 text-muted-foreground" />
                              )}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`font-semibold ${partner.balance > 0 ? 'text-gold-shimmer' : 'text-muted-foreground'}`}>
                            €{(partner.balance || 0).toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {partner.is_verified ? (
                            <span className="flex items-center gap-1 text-accent text-sm">
                              <CheckCircle className="w-4 h-4" /> Verificato
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-yellow-500 text-sm">
                              <XCircle className="w-4 h-4" /> Non verificato
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setBillingPartner(partner)}
                              className="h-8 px-3 border-white/10 hover:bg-white/5"
                              data-testid={`billing-${partner.id}`}
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              Dati
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openCreditModal(partner)}
                              className="h-8 px-3 btn-gold"
                              data-testid={`add-credit-${partner.id}`}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Fondi
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openPayoutModal(partner)}
                              className="h-8 px-3 bg-[#fc0301] hover:bg-[#fc0301]/90 text-white"
                              disabled={partner.balance <= 0}
                              data-testid={`payout-${partner.id}`}
                            >
                              <ArrowUpRight className="w-3 h-3 mr-1" />
                              Payout
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </motion.div>
          </TabsContent>

          {/* Payout Requests Tab */}
          <TabsContent value="payouts">
            <motion.div
              className="glass-card overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {pendingPayouts.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500/50 mb-4" />
                  <p className="text-lg font-medium">Nessuna richiesta in attesa</p>
                  <p className="text-sm text-muted-foreground mt-1">Tutte le richieste di payout sono state processate.</p>
                </div>
              ) : (
                <Table data-testid="payouts-table">
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead>Data</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead>Importo</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPayouts.map((payout) => {
                      const partner = partners.find(p => p.id === payout.user_id);
                      return (
                        <TableRow key={payout.id} className="border-white/5">
                          <TableCell className="text-sm">
                            {new Date(payout.created_at).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{partner ? `${partner.first_name} ${partner.last_name}` : '—'}</p>
                              <p className="text-xs text-muted-foreground">{partner?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-lg font-semibold text-gold-shimmer">€{payout.amount.toFixed(2)}</span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {payout.description || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setBillingPartner(partner)}
                                className="h-8 px-3 border-white/10 hover:bg-white/5"
                                data-testid={`billing-payout-${payout.id}`}
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Dati
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleAcceptPayout(payout.id)}
                                className="h-8 px-4 btn-gold"
                                disabled={processingPayoutId === payout.id}
                                data-testid={`accept-payout-${payout.id}`}
                              >
                                {processingPayoutId === payout.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Approva
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectPayout(payout.id)}
                                className="h-8 px-4 border-red-500/30 text-red-500 hover:bg-red-500/10"
                                disabled={processingPayoutId === payout.id}
                                data-testid={`reject-payout-${payout.id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Rifiuta
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </motion.div>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <motion.div
              className="glass-card overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Table data-testid="referrals-table">
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Azienda</TableHead>
                    <TableHead>Referente</TableHead>
                    <TableHead>Team Size</TableHead>
                    <TableHead>Segnalato da</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReferrals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Nessuna segnalazione trovata
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReferrals.map((referral) => (
                      <TableRow key={referral.id} className="border-white/5">
                        <TableCell>
                          <p className="font-medium">{referral.company_name}</p>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm">{referral.contact_name}</p>
                            <p className="text-xs text-muted-foreground">{referral.contact_email}</p>
                            <p className="text-xs text-muted-foreground">{referral.contact_phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {referral.team_size ? (
                            <span>{referral.team_size} venditori</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <code className="px-2 py-1 rounded bg-[#fcb900]/10 text-[#fcb900] text-xs font-mono">
                            {referral.referred_by_code || "direct"}
                          </code>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(referral.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={referral.status}
                            onValueChange={(value) => handleUpdateReferralStatus(referral.id, value)}
                          >
                            <SelectTrigger className="w-[200px] h-8 text-xs bg-black/30 border-white/10" data-testid={`status-select-${referral.id}`}>
                              <SelectValue>
                                {referral.status === 'pending' ? 'In gestione' :
                                 referral.status === 'in_gestione' ? 'In gestione' :
                                 referral.status === 'sottoscrizione_avvenuta' ? 'Sottoscrizione avvenuta' :
                                 referral.status === 'vendita_confermata' ? 'Vendita confermata' :
                                 referral.status === 'perso' ? 'Perso' : referral.status}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-card border-white/10">
                              <SelectItem value="in_gestione">
                                <span className="text-blue-400">In gestione</span>
                              </SelectItem>
                              <SelectItem value="sottoscrizione_avvenuta">
                                <span className="text-green-400">Sottoscrizione avvenuta</span>
                              </SelectItem>
                              <SelectItem value="vendita_confermata">
                                <span className="text-emerald-300">Vendita confermata</span>
                              </SelectItem>
                              <SelectItem value="perso">
                                <span className="text-red-400">Perso</span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </motion.div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <motion.div
              className="glass-card overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Table data-testid="transactions-table">
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead>Data</TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Importo</TableHead>
                    <TableHead>Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Nessun movimento presente
                      </TableCell>
                    </TableRow>
                  ) : (
                    allTransactions.map((transaction) => {
                      const partner = partners.find(p => p.id === transaction.user_id);
                      return (
                        <TableRow key={transaction.id} className="border-white/5">
                          <TableCell className="text-sm">
                            {new Date(transaction.created_at).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            {partner ? `${partner.first_name} ${partner.last_name}` : transaction.user_id.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {transaction.type === 'credit' ? (
                                <span className="flex items-center gap-1 text-green-500">
                                  <ArrowDownRight className="w-4 h-4" /> Accredito
                                </span>
                              ) : transaction.type === 'payout_request' ? (
                                <span className="flex items-center gap-1 text-yellow-500">
                                  <Clock className="w-4 h-4" /> Richiesta Payout
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-red-500">
                                  <ArrowUpRight className="w-4 h-4" /> Payout
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {transaction.description}
                          </TableCell>
                          <TableCell>
                            <span className={`font-semibold ${
                              transaction.type === 'credit' ? 'text-green-500' : 'text-red-500'
                            }`}>
                              {transaction.type === 'credit' ? '+' : '-'}€{transaction.amount.toFixed(2)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              transaction.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                              transaction.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                              'bg-red-500/20 text-red-500'
                            }`}>
                              {transaction.status === 'completed' ? 'Completato' :
                               transaction.status === 'pending' ? 'In attesa' : 'Rifiutato'}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Credit Modal */}
      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent className="sm:max-w-md bg-card border-white/10" data-testid="credit-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Aggiungi Fondi a {selectedPartner?.first_name} {selectedPartner?.last_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCredit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(e.target.value)}
                className="bg-black/20 border-white/10 h-11"
                placeholder="Es: 1200.00"
                required
                data-testid="credit-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={transactionDescription}
                onChange={(e) => setTransactionDescription(e.target.value)}
                className="bg-black/20 border-white/10 h-11"
                placeholder="Es: Commissione referral XYZ Srl"
                data-testid="credit-description"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-11 btn-gold"
              disabled={isLoading}
              data-testid="submit-credit-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Aggiungi Fondi
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="sm:max-w-md bg-card border-white/10" data-testid="payout-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              Effettua Payout per {selectedPartner?.first_name} {selectedPartner?.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 p-3 rounded-lg bg-[#fcb900]/10 border border-[#fcb900]/20">
            <p className="text-sm text-muted-foreground">Saldo disponibile</p>
            <p className="text-xl font-bold text-gold-shimmer">€{(selectedPartner?.balance || 0).toFixed(2)}</p>
          </div>
          <form onSubmit={handleProcessPayout} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={selectedPartner?.balance || 0}
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(e.target.value)}
                className="bg-black/20 border-white/10 h-11"
                placeholder="Es: 500.00"
                required
                data-testid="payout-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={transactionDescription}
                onChange={(e) => setTransactionDescription(e.target.value)}
                className="bg-black/20 border-white/10 h-11"
                placeholder="Es: Bonifico del 24/03/2026"
                data-testid="payout-description"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-11 bg-[#fc0301] hover:bg-[#fc0301]/90"
              disabled={isLoading}
              data-testid="submit-payout-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
              Effettua Payout
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Billing Info Dialog */}
      <Dialog open={!!billingPartner} onOpenChange={() => setBillingPartner(null)}>
        <DialogContent className="glass-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dati di Fatturazione</DialogTitle>
          </DialogHeader>
          {billingPartner && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                <p className="text-sm font-medium text-[#fcb900]">{billingPartner.first_name} {billingPartner.last_name}</p>
                <p className="text-xs text-muted-foreground">{billingPartner.email}</p>
              </div>
              {[
                { label: "Ragione Sociale", value: billingPartner.billing_info?.company_name },
                { label: "P.IVA / Codice Fiscale", value: billingPartner.billing_info?.tax_code },
                { label: "Indirizzo", value: billingPartner.billing_info?.address },
                { label: "SDI / PEC", value: billingPartner.billing_info?.sdi_pec },
                { label: "IBAN", value: billingPartner.billing_info?.iban },
              ].map((field) => (
                <div key={field.label} className="space-y-1">
                  <p className="text-xs text-muted-foreground">{field.label}</p>
                  <p className={`text-sm font-medium ${field.value ? 'text-foreground' : 'text-muted-foreground/50 italic'}`} data-testid={`billing-field-${field.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                    {field.value || "Non compilato"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
